import {type MetaFunction} from '@remix-run/node';
import {createMetaHandler} from '#/app/helpers/createMetaHandler.js';

export const meta: MetaFunction = createMetaHandler(() => {
  return [{title: 'Terms of Use - Ufleet'}];
});

const TermsOfUse = () => {
  return (
    <div className="mx-auto w-2/5 py-32 text-lg leading-relaxed text-gray-600">
      <h1 className="mb-32 text-center">Terms of use</h1>
      <div className="space-y-8">
        <p>Last Updated: October 1, 2021</p>
        <p>
          Thank you for using Ufleet. These terms of use (the “Terms”) govern your access to and use of Ufleet (“we” or “our”) websites and
          services (the “Services”). Please read them carefully before using the Services.
        </p>
        <p>
          By using the Services you are agreeing to these Terms. If you are using the Services on behalf of an organization, you are
          agreeing to these Terms for that organization and representing that you have the authority to bind that organization to these
          terms. In that case, “you” and “your” will refer to that organization.
        </p>
        <p>
          You may use the Services only in accordance with these Terms. You may use the Services only if you have the legal power and
          capacity to form a contract with Ufleet. The Services will continue to evolve as we refine features and functionality. We may
          terminate, suspend, or modify the Services, in general or with respect to you, from time to time without cause or prior notice. We
          may also delete any content or data from the Services at our discretion.
        </p>
        <p>
          All documents and information on the Ufleet website are protected by copyright. Except as specifically permitted herein, no
          portion of the documents or information on this website may be reproduced in any form or by any means without the express written
          consent of Ufleet.
        </p>
        <h2 className="pb-4 pt-12 text-3xl font-bold text-gray-700">Content and Liability Disclaimer</h2>
        <p>
          Ufleet shall not be responsible for any errors or omissions contained on any Ufleet website and reserves the right to make changes
          anytime without notice. Mention of non-Ufleet products or services is provided for informational purposes only and constitutes
          neither an endorsement nor a recommendation by Ufleet. All Ufleet and third-party information provided on any Ufleet website is
          provided on an “as is” basis.
        </p>
        <p>
          <strong>
            UFLEET DISCLAIMS ALL WARRANTIES, EXPRESSED OR IMPLIED, WITH REGARD TO ANY INFORMATION (INCLUDING ANY SOFTWARE, PRODUCTS, OR
            SERVICES) PROVIDED ON ANY UFLEET WEBSITE, INCLUDING THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR
            PURPOSE, AND NON-INFRINGEMENT.
          </strong>
        </p>
        <p>Some jurisdictions do not allow the exclusion of implied warranties, so the above exclusion may not apply to you.</p>
        <p>
          <strong>
            IN NO EVENT SHALL UFLEET BE LIABLE FOR ANY DAMAGES WHATSOEVER, AND IN PARTICULAR UFLEET SHALL NOT BE LIABLE FOR DIRECT, SPECIAL,
            INDIRECT, CONSEQUENTIAL, OR INCIDENTAL DAMAGES, OR DAMAGES FOR LOST PROFITS, LOSS OF REVENUE OR LOSS OF USE, COST OF REPLACEMENT
            GOODS, LOSS OR DAMAGE TO DATA ARISING OUT OF THE USE OR INABILITY TO USE ANY UFLEET WEBSITE, ANY UFLEET PRODUCT OR SERVICE. THIS
            INCLUDES DAMAGES ARISING FROM USE OF OR IN RELIANCE ON THE DOCUMENTS OR INFORMATION PRESENT ON ANY UFLEET WEBSITE (INCLUDING ANY
            INFORMATION POSTED OR PLACED BY ANYONE OTHER THAN UFLEET), EVEN IF UFLEET HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </strong>
        </p>
        <h2 className="pb-4 pt-12 text-3xl font-bold text-gray-700">Release of Information For Legal Reasons</h2>
        <p>
          We may release information concerning your use of the website as required by law and when we believe in good faith that such
          release is appropriate to protect our rights and/or comply with a judicial proceeding, court order or legal process served on us
          or the website.
        </p>
        <h2 className="pb-4 pt-12 text-3xl font-bold text-gray-700">Feedback Information</h2>
        <p>
          Any information provided to Ufleet in connection with any Ufleet website shall be provided by the submitter and received by Ufleet
          on a non-confidential basis. Such information shall be considered non-confidential and property of Ufleet. By submitting any such
          information to Ufleet you agree to a no-charge assignment to Ufleet of all worldwide rights, title, and interest in copyrights and
          other intellectual property rights to the information. Ufleet shall be free to use such information on an unrestricted basis.
        </p>
        <h2 className="pb-4 pt-12 text-3xl font-bold text-gray-700">License to Use Our Services</h2>
        <p>
          Subject to these Terms, we grant to you a limited, non-exclusive, non-transferable license to use our Services for your internal
          use and not for resale or further distribution. Your right to use our Services is limited by all terms and conditions set forth in
          these Terms. Except for your pre-existing rights and this license granted to you, we and our licensors retain all right, title and
          interest in and to our Services, including all related intellectual property rights. Our Services are protected by applicable
          international intellectual property laws and treaties.
        </p>
        <p>
          Except as otherwise explicitly provided in these Terms or as may be expressly permitted by applicable law, you will not, and will
          not permit or authorize any third party to: (i) reproduce, modify, translate, enhance, decompile, disassemble, reverse engineer or
          create derivative works of any of our Services; (ii) rent, lease or sublicense access to any of our Services; or (iii) circumvent
          or disable any security or technological features or measures of our Services.
        </p>
        <p>
          Certain of our Services are provided at no cost and certain Services are subject to various fees which are based on usage and are
          non-refundable.
        </p>
        <p>
          If you are paying by a credit card, you authorize the card issuer to pay any amounts set forth herein and authorize Ufleet (or our
          billing agent) to charge your credit card account until you or we cancel or terminate your pricing tier; provided that if payment
          is not received from your credit card issuer, you agree to pay all amounts due upon demand. You must provide current, complete and
          accurate billing and credit card information. You agree to pay all costs of collection, including attorney's fees and costs, on
          any outstanding balance. In certain instances, the issuer of your credit card may charge you a foreign transaction fee or related
          charges, which you will be responsible to pay.
        </p>
        <p>
          You will be responsible for payment of any applicable sales, use and other taxes and all applicable export and import fees,
          customs duties and similar charges (other than taxes based on Ufleet’s income), and any related penalties and interest for the
          grant of access rights hereunder, or the delivery of related services. You will make all required payments to Ufleet free and
          clear of, and without reduction for, any withholding taxes. Any such taxes imposed on payments to Ufleet will be your sole
          responsibility, and you will, upon Ufleet’s request, provide Ufleet with official receipts issued by appropriate taxing
          authorities, or such other evidence as Ufleet may reasonably request, to establish that such taxes have been paid.
        </p>
        <h2 className="pb-4 pt-12 text-3xl font-bold text-gray-700">Access to Our Services</h2>
        <p>
          We do not provide you with the equipment to access our Services. You are responsible for all fees charged by third parties related
          to your access and use of our Services (e.g., charges by Internet service providers).
        </p>
        <p>
          We reserve the right to modify or discontinue, temporarily or permanently, all or any portion of our Services without notice. We
          will not be liable to you or to any third party for any modification, suspension, or discontinuance of all or any portion of our
          Services.
        </p>
        <p>
          We also reserve the right, in our sole discretion, to reject, refuse to post, or remove any material that you post or submit for
          posting, and to restrict, suspend, or terminate your access to our Services at any time, for any or no reason, with or without
          prior notice, and without liability.
        </p>
        <h2 className="pb-4 pt-12 text-3xl font-bold text-gray-700">Restrictions</h2>
        <p>
          You must comply with all applicable laws when using our Services. Except as may be expressly permitted by applicable law, or as
          Ufleet may authorize expressly in writing, you will not, and will not permit anyone else to: (i) store, copy, modify, distribute,
          or resell any of the information; audio, visual, and audiovisual works, or other content made available on our Services
          (collectively, “Service Content”) or compile or collect any Service Content as part of a database or other work; (ii) use any
          automated tool (e.g., robots, spiders) to access or use our Services or to store, copy, modify, distribute, or resell any Service
          Content; (iii) rent, lease, or sublicense your access to our Services to another person; (iv) use any Services or Service Content
          for any purpose except for your own internal use; (v) circumvent or disable any digital rights management, usage rules, or other
          security features of our Services; (vi) use our Services in a manner that overburdens, or that threatens the integrity,
          performance, or availability of, our Services; or (vii) remove, alter, or obscure any proprietary notices (including copyright and
          trademark notices) on any portion of our Services or any Service Content.
        </p>

        <h2 className="pb-4 pt-12 text-3xl font-bold text-gray-700"> Restricted Areas of the Services</h2>
        <p>
          Certain parts of our Services, including account management features, may be password-restricted to registered users or other
          authorized persons (“Password-Protected Areas”). If you are authorized to gain access to any Password-Protected Areas, you agree
          that you are entirely responsible for maintaining the confidentiality of your password, and agree to notify us if the password is
          lost, stolen, disclosed to an unauthorized third party, or otherwise may have been compromised. You agree that you are entirely
          responsible for any and all activities that occur under your account, whether or not you are the individual who undertakes such
          activities. You agree to immediately notify us of any unauthorized use of your account or any other breach of security in relation
          to your password or our Services that is known to you.
        </p>
        <h2 className="pb-4 pt-12 text-3xl font-bold text-gray-700">Products</h2>
        <p>
          Ufleet’s obligations with respect to its products and services are governed solely by the license and/or service agreements under
          which they are provided. If you obtain a product or service from Ufleet from this website that is provided without an agreement,
          that product or service is provided “AS-IS” with no warranties whatsoever, express or implied, and your use of that product or
          service is at your own risk.
        </p>
        <h2 className="pb-4 pt-12 text-3xl font-bold text-gray-700">Links to Third-Party Sites</h2>
        <p>
          The Ufleet websites may contain links to third-party sites. Access to any website linked to any Ufleet website is not the
          responsibility of Ufleet and Ufleet is not responsible for the accuracy, or reliability of any content on such websites. Further,
          the presence of a link to a third-party site does not mean that Ufleet endorses that site, its products, or views expressed there.
          Ufleet provides these links merely for convenience and the presence of such third-party links are not an endorsement or
          recommendation by Ufleet.{' '}
        </p>

        <h2 className="pb-4 pt-12 text-3xl font-bold text-gray-700">LIMITATION OF LIABILITY</h2>
        <p>
          <strong>
            IN NO EVENT WILL UFLEET BE LIABLE TO ANY PARTY FOR ANY DIRECT, INDIRECT, INCIDENTAL, EXEMPLARY, SPECIAL OR OTHER CONSEQUENTIAL
            DAMAGES FOR ANY USE OF THIS WEBSITE, OR ON ANY OTHER HYPERLINKED WEBSITE, INCLUDING, WITHOUT LIMITATION, ANY LOST PROFITS,
            BUSINESS INTERRUPTION, LOSS OF PROGRAMS OR OTHER DATA ON YOUR INFORMATION HANDLING SYSTEM OR OTHERWISE, EVEN IF UFLEET IS
            EXPRESSLY ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </strong>
        </p>

        <h2 className="pb-4 pt-12 text-3xl font-bold text-gray-700">Governing Law and Jurisdiction</h2>
        <p>
          Enforcement of any dispute relating to these Terms, which cannot be settled amicably and through negotiations, shall be referred
          to the The Court of Arbitration of the Hamburg Chamber of Commerce, pursuant to its Rules for cases based on arbitration
          agreements, whereas German law shall be applicable. The arbitrator's award shall be final and binding and may be entered as a
          judgment in any court of competent jurisdiction. The parties each agree that any dispute resolution proceedings will be conducted
          only on an individual basis and not in a class, consolidated or representative action.
        </p>
        <p>
          Our failure to enforce any right or provision in these Terms will not constitute a waiver of such right or provision unless
          acknowledged and agreed to by us in writing. In the event that a court of competent jurisdiction finds any provision of these
          Terms to be illegal, invalid, or unenforceable, the remaining provisions will remain in full force and effect.
        </p>
        <p>The terms and conditions which by their nature are intended to survive termination of these Terms shall survive.</p>
        <h2 className="pb-4 pt-12 text-3xl font-bold text-gray-700">Trademarks</h2>
        <p>
          The trademarks, logos, and service marks (collectively “Trademarks”) appearing on the Ufleet website are the property of Ufleet,
          Inc. Nothing contained on the Ufleet website should be construed as granting any license or right to use any Trademark without the
          prior written permission of the party that owns the Trademark.
        </p>

        <h2 className="pb-4 pt-12 text-3xl font-bold text-gray-700">Contacting Us</h2>
        <p>If you have any questions or concerns about our Services or these Terms, you may contact us at:</p>
        <p>
          <strong>Ufleet Ltd.</strong>
          <br />
          Torstraße 182
          <br />
          10115 Berlin
          <br />
          Germany
          <br />
          <br />
          or by email at
          {' '}
          <a className="_cta underline" href="mailto:contact@ufleet.io">
            contact@ufleet.io
          </a>
          <br />
        </p>
      </div>
    </div>
  );
};

export default TermsOfUse;
